import { NgModule, CUSTOM_ELEMENTS_SCHEMA, ErrorHandler, inject, provideAppInitializer, provideZoneChangeDetection } from '@angular/core';
import { ErrorStateMatcher, ShowOnDirtyErrorStateMatcher } from '@angular/material/core';
import { BrowserModule } from '@angular/platform-browser';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient } from '@angular/common/http';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { GlobalModule } from './global-module';
import { HttpModule } from './http-module';
import { ServerLoginService } from './server-login.service';
import { LoginComponent } from './login/login.component';
import { MarkdownModule, MARKED_OPTIONS, provideMarkdown, SANITIZE } from 'ngx-markdown';
import DOMPurify from 'dompurify';
import { AccountSettingsComponent } from './account-settings/account-settings.component';
import { MessageBarComponent } from './message-bar/message-bar.component';
import { CaptureFormComponent } from './dynamic/capture-form/capture-form.component';
import { OrganizationTreeComponent } from './dynamic/organization-tree/organization-tree.component';
import { AnalysisChartComponent } from './dynamic/analysis-chart/analysis-chart.component';
import { InstructionsComponent } from './instructions/instructions.component';
import { ExternalUrlDirective } from './external-url.directive';
import { EmptyLinkComponent } from './empty-link/empty-link.component';
import { DefaultHtmlComponent } from './default-html/default-html.component';
import { ComponentListComponent } from './component-list/component-list.component';
import { LayoutComponent } from './layout/layout.component';
import { FormlyModule } from '@ngx-formly/core';
import { FormlyMaterialModule } from '@ngx-formly/material';
import { FormlyMatDatepickerModule } from '@ngx-formly/material/datepicker';
import { FormlyMatToggleModule } from '@ngx-formly/material/toggle';
import { FormlyMatTextAreaModule } from '@ngx-formly/material/textarea';
import { ColorPickerModule } from 'ngx-color-picker';
import { GroupWrapperComponent, ColumnWrapperComponent, FooterWrapperComponent } from './dynamic/capture-form/group-wrapper.component';
import { FileChecklistComponent } from './dynamic/capture-form/file-checklist.component';
import { GroupListComponent, AdditionalDataComponent } from './dynamic/group-list/group-list.component';
import { ReviewFormComponent } from './dynamic/review-form/review-form.component';
import { AlertComponent } from './dynamic/alert/alert.component';
import { PaymentFormComponent } from './dynamic/payment-form/payment-form.component';
import { AddRowComponent } from './dynamic/add-row/add-row.component';
import { DialogComponent } from './dynamic/dialog/dialog.component';
import { StepperComponent } from './dynamic/stepper/stepper.component';
import { StepperHeaderComponent } from './dynamic/stepper/stepper-header.component';
import { ColorPickerComponent } from './dynamic/color-picker/color-picker.component';
import { LayoutDialogComponent } from './dynamic/dialog/layout-dialog.component';
import { SendMessageDialogComponent } from './dynamic/dialog/send-message-dialog.component';
import { TypeheadOptionComponent } from './dynamic/typehead-option/typehead-option.component';
import { ProgressIndicatorComponent } from './progress-indicator/progress-indicator.component';
import { ErrorPageComponent } from './error-page/error-page.component';
import { VerifyComponent } from './verify/verify.component';
import { ApplyStatusComponent } from './apply-status/apply-status.component';
import { IconComponent } from './dynamic/icon/icon.component';
import { ButtonComponent } from './dynamic/button/button.component';
import { PhoneComponent } from './dynamic/phone/phone.component';
import { AddonWrapperComponent } from './dynamic/addon-wrapper/addon-wrapper.component'
import { ButtonRowComponent } from './dynamic/button-row/button-row.component';
import { RadioGroupComponent } from './dynamic/radio-group/radio-group.component';
import { DisplaySelectionComponent } from './display-selection/display-selection.component';
import { NgxMaskDirective, NgxMaskPipe, provideEnvironmentNgxMask } from 'ngx-mask';
import { AupComponent } from './aup/aup.component';
import { MutaraCoderDirective } from './mutara-coder.directive';
import { MessageComponent } from './dynamic/message/message.component';
import { DataDumpComponent } from './data-dump/data-dump.component';
import { AgreementComponent } from './agreement/agreement.component';
import { ComponentWrapperComponent } from './dynamic/component-wrapper/component-wrapper.component';
import { addonsExtension } from './addons.extension';
import { EditWrapperComponent } from './dynamic/edit-wrapper/edit-wrapper.component';
import { MutaraErrroHandler } from './error-handler/error-handler';
import { CartComponent } from './cart/cart.component';
import { MutaraFileComponent } from './file-handler/file-type.component';
import { CombineValuesComponent } from './dynamic/combine-values/combine-values.component';
import { RadioControllerComponent } from './dynamic/radio-controller/radio-controller.component';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_CARD_CONFIG } from '@angular/material/card';
import { SubstitutionPipe } from './substitution.pipe';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { ReactiveFormsModule } from '@angular/forms';

function sanitizeHtml(html: string): string {
  DOMPurify.setConfig({ IN_PLACE: true  });
  return DOMPurify.sanitize(html);
}

@NgModule({
  declarations: [
    AppComponent,
    LoginComponent,
    CartComponent,
    AccountSettingsComponent,
    MessageBarComponent,
    CaptureFormComponent,
    OrganizationTreeComponent,
    AnalysisChartComponent,
    InstructionsComponent,
    ExternalUrlDirective,
    EmptyLinkComponent,
    DefaultHtmlComponent,
    ComponentListComponent,
    LayoutComponent,
    GroupWrapperComponent,
    ColumnWrapperComponent,
    FooterWrapperComponent,
    FileChecklistComponent,
    RadioGroupComponent,
    GroupListComponent,
    AdditionalDataComponent,
    ReviewFormComponent,
    AlertComponent,
    PaymentFormComponent,
    AddRowComponent,
    DialogComponent,
    StepperComponent,
    StepperHeaderComponent,
    ColorPickerComponent,
    LayoutDialogComponent,
    SendMessageDialogComponent,
    TypeheadOptionComponent,
    ProgressIndicatorComponent,
    ErrorPageComponent,
    VerifyComponent,
    ApplyStatusComponent,
    IconComponent,
    ButtonComponent,
    PhoneComponent,
    AddonWrapperComponent,
    ButtonRowComponent,
    DisplaySelectionComponent,
    AupComponent,
    MutaraCoderDirective,
    MessageComponent,
    DataDumpComponent,
    AgreementComponent,
    EditWrapperComponent,
    MutaraFileComponent,
    CombineValuesComponent,
    RadioControllerComponent,
    SubstitutionPipe,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    GlobalModule,
    HttpModule,
    FormlyMatDatepickerModule,
    FormlyMatToggleModule,
    FormlyMatTextAreaModule,
    FormlyMaterialModule,
    NgxMaskDirective,
    NgxMaskPipe,
    ColorPickerModule,
    MarkdownModule.forRoot({
      loader: HttpClient,
      markedOptions: {
        provide: MARKED_OPTIONS,
        useValue: {
          breaks: true,
        }
      }
    }),
    FormlyModule.forRoot({
      types: [
        { name: 'addrow', component: AddRowComponent },
        { name: 'dialog', component: DialogComponent },
        { name: 'layout-dialog', component: LayoutDialogComponent },
        { name: 'send-message-dialog', component: SendMessageDialogComponent },
        { name: 'stepper', component: StepperComponent },
        { name: 'stepper-header', component: StepperHeaderComponent },
        { name: 'icon', component: IconComponent },
        { name: 'typeahead', component: TypeheadOptionComponent },
        { name: 'button', component: ButtonComponent },
        { name: 'message', component: MessageComponent },
        { name: 'radio-group', component: RadioGroupComponent },
        { name: 'phone', component: PhoneComponent },
        { name: 'application', component: LayoutComponent },
        { name: 'compact' },
        { name: 'color-picker', component: ColorPickerComponent },
        { name: 'file', component: MutaraFileComponent },
        { name: 'radio-controller', component: RadioControllerComponent },
        { name: 'alert', component: AlertComponent },
        { name: 'review', component: ReviewFormComponent },
      ],
      wrappers: [
        { name: 'columns', component: ColumnWrapperComponent },
        { name: 'footer', component: FooterWrapperComponent },
        { name: 'group', component: GroupWrapperComponent },
        { name: 'table', component: GroupListComponent },
        { name: 'combine-values', component: CombineValuesComponent },
        { name: 'datadisplay', component: DataDumpComponent },
        { name: 'agreement', component: AgreementComponent },
        { name: 'button-row', component: ButtonRowComponent },
        { name: 'file-checklist', component: FileChecklistComponent },
        { name: 'addons', component: AddonWrapperComponent },
        { name: 'delegate', component: ComponentWrapperComponent },
        { name: 'editor', component: EditWrapperComponent },
        { name: 'payment-form', component: PaymentFormComponent },
      ],
      extensions: [
        { name: 'addons', extension: { onPopulate: addonsExtension } },
      ],
      extras: {
        checkExpressionOn: 'changeDetectionCheck',
        resetFieldOnHide: false
      },
      validationMessages: [
        { name: 'matDatepickerMin', message: 'Date earlier than allowed' },
        { name: 'matDatepickerMax', message: 'Date later than allowed' }
      ],
    }),
    FormlyModule.forRoot(),
    ReactiveFormsModule,
  ],
  providers: [
    SubstitutionPipe,
    provideHttpClient(),
    provideMarkdown({
      loader: HttpClient, 
      sanitize: {
        provide: SANITIZE,
        useValue: sanitizeHtml,
      }
    }),
    provideAppInitializer(() => {
      const initializerFn: any = ((settings) => settings.load())(inject(ServerLoginService));
      return initializerFn();
    }),
    provideZoneChangeDetection({ eventCoalescing: true }), // We need zone detection
    provideEnvironmentNgxMask(),
    // { provide: OVERLAY_DEFAULT_CONFIG, useValue: { usePopover: false }},
    { provide: ErrorHandler, useClass: MutaraErrroHandler },
    { provide: ErrorStateMatcher, useClass: ShowOnDirtyErrorStateMatcher },
    { provide: MAT_FORM_FIELD_DEFAULT_OPTIONS, useValue: { color: 'primary', floatLabel: 'always', appearance: 'outline' } },
    { provide: MAT_CARD_CONFIG, useValue: { appearance: 'outline' } },
    { provide: MAT_RADIO_DEFAULT_OPTIONS, useValue: { color: 'primary', appearance: 'outline' } },
    { provide: HTTP_INTERCEPTORS, useClass: ServerLoginService, multi: true },
  ],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class AppModule { }